import React, { useEffect, useState } from 'react';
import { Steps, Button, Space } from 'antd';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { decryptData, encryptData } from '../../actions/crypto';

const { Step } = Steps;

interface StepItem {
    title: string;
    content: React.ReactNode;
}

interface StepperProps {
    steps: StepItem[];
    queryKey?: string;
    onNext?: (currentStep: number) => Promise<boolean>;
    onPrev?: (currentStep: number) => Promise<boolean>;
    onSubmit?: () => Promise<boolean>;
    loading?: boolean;
}

const Stepper: React.FC<StepperProps> = ({
    steps,
    queryKey = 'step',
    onNext,
    onPrev,
    onSubmit,
    loading = false,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [current, setCurrent] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();

    const getQueryStep = (): number => {
        const params = new URLSearchParams(location.search);
        const step = parseInt(params.get(queryKey) ? decryptData(params.get(queryKey)) : '0', 10);
        return !isNaN(step) && step >= 0 && step < steps.length ? step : 0;
    };

    useEffect(() => {
        setCurrent(getQueryStep());
    }, [location.search]);

    const goToStep = (step: number, vendor = undefined) => {
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.set(queryKey, encryptData(String(step)));
        if (vendor) {
            newParams.set('vendor', vendor);
        }
        navigate({ search: newParams.toString() });
        setCurrent(step);
    };

    const next = async () => {
        if (onNext) {
            const res: any = await onNext(current);
            if (res) {
                const nextStep = current + 1;
                if (nextStep < steps.length) {
                    goToStep(nextStep, current == 0 ? res._id : undefined);
                }
            }
        } else {
            const nextStep = current + 1;
            if (nextStep < steps.length) {
                goToStep(nextStep);
            }
        }
    };

    const prev = async () => {
        if (onPrev) {
            const res = await onPrev(current);
            if (res) {
                const prevStep = current - 1;
                if (prevStep >= 0) {
                    goToStep(prevStep);
                }
            }

        } else {
            const prevStep = current - 1;
            if (prevStep >= 0) {
                goToStep(prevStep);
            }
        }

    };

    const handleSubmit = async () => {
        if (onSubmit) {
            const res = await onSubmit();
            if (res) {
                navigate('/success');
            }
        }
    };

    return (
        <div>
            <Steps current={current} style={{ marginBottom: 24 }} size='small'>
                {steps.map((step, index) => (
                    <Step key={step.title} title={step.title} disabled={index > current} />
                ))}
            </Steps>

            <div style={{ padding: 24, border: '1px solid #f0f0f0', borderRadius: 8 }}>
                {steps[current]?.content}
            </div>

            <div className='flex justify-end mt-4'>
                <Space style={{ marginTop: 24 }}>
                    {current > 0 && (
                        <Button onClick={prev} loading={loading}>Previous</Button>
                    )}
                    {current < steps.length - 1 && (
                        <Button type="primary" onClick={next} loading={loading}>Save & Next</Button>
                    )}
                    {current === steps.length - 1 && (
                        <Button type="primary" onClick={handleSubmit} loading={loading}>Submit</Button>
                    )}
                </Space>
            </div>
        </div>
    );
};

export default Stepper;
